import axiosInstance from "../services/api/axiosClient";

export const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5 MB

export interface S3UploadResult {
	key: string;
	fileName: string;
	fileSize: number;
	contentType: string;
}

/** Returns true if the stored value is an S3 key (not a data URL or presigned URL). */
export function isS3Key(value: string | undefined): boolean {
	return !!value && value.startsWith("documents/");
}

/** Returns true if the stored value is a base64 data URL. */
export function isDataUrl(value: string | undefined): boolean {
	return !!value && value.startsWith("data:");
}

/**
 * Uploads a file to S3 via the BFF `/api/documents/raw` endpoint.
 *
 * Wire shape: raw body (not multipart/form-data). The File is the request
 * body verbatim; the filename rides in an `X-Filename` header (URL-encoded
 * to survive non-ASCII characters), and the Content-Type is the file's
 * MIME type.
 *
 * Why raw body instead of multipart: Bun's `Request.formData()` parser
 * corrupts binary payloads — every byte 0x80–0xFF is round-tripped through
 * UTF-8 decoding and replaced with U+FFFD (`EF BF BD`). PDFs uploaded that
 * way have a valid ASCII header but unreadable binary streams (blank
 * pages). The bug persisted across both multer/busboy and native
 * `formData()` and was still present in Bun 1.3.14, so we bypass multipart
 * parsing entirely. The BFF handler uses `request.arrayBuffer()` instead.
 *
 * Returns the permanent S3 key (not the presigned URL, which expires).
 *
 * @throws Error if the file exceeds the 5 MB limit or the upload fails.
 */
export async function uploadFileToS3(file: File): Promise<S3UploadResult> {
	if (file.size > MAX_UPLOAD_SIZE) {
		const sizeMB = (file.size / 1024 / 1024).toFixed(1);
		throw new Error(
			`File is too large (${sizeMB} MB). Maximum allowed size is 5 MB.`,
		);
	}

	// File.type may be empty for files dragged in from the OS without an
	// associated MIME — fall back so the BFF and S3 always see a valid value.
	const contentType = file.type || "application/octet-stream";

	const result = await axiosInstance.post<S3UploadResult>(
		"/api/documents/raw",
		file,
		{
			headers: {
				"Content-Type": contentType,
				// encodeURIComponent so accented characters / spaces survive the
				// HTTP header round-trip; the BFF decodeURIComponents on receive.
				"X-Filename": encodeURIComponent(file.name),
			},
			// axios will try to JSON-serialize the body unless we tell it not to.
			transformRequest: [(data) => data],
		},
	);

	// The axios response interceptor unwraps ApiResponse.data already.
	const data = result as unknown as S3UploadResult & { url?: string };

	return {
		key: data.key,
		fileName: data.fileName,
		fileSize: data.fileSize,
		contentType: data.contentType,
	};
}

/**
 * Resolves a stored file value to a viewable URL.
 * - Data URLs are returned as-is.
 * - S3 keys are resolved to presigned URLs via the API.
 * - Other URLs (presigned, http) are returned as-is.
 */
export async function resolveFileUrl(
	value: string | undefined,
): Promise<string | null> {
	if (!value) return null;
	if (isDataUrl(value)) return value;
	if (isS3Key(value)) {
		const data = (await axiosInstance.get(
			`/api/documents/signed-url/${value}`,
		)) as unknown as { url: string };
		return data.url;
	}
	// Legacy presigned URL or plain http URL — return as-is
	return value;
}
